import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CircleStackIcon,
  ClipboardDocumentListIcon,
  CpuChipIcon,
  CreditCardIcon,
  KeyIcon,
  SignalIcon,
  UsersIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores';

interface Overview {
  users: number;
  admins: number;
  editors: number;
  viewers: number;
  liveConnections: number;
  liveUsers: number;
  activityCount: number;
  ai: { total: number; active: number };
  storage: { total: number; active: number };
  payment: { total: number; active: number };
  social: { total: number; active: number };
  crypto: { total: number; active: number };
  prompts: number;
}

async function fetchJson<T>(url: string, token: string | null): Promise<T | null> {
  try {
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

function countActive(items: Array<{ isActive?: boolean }> | null) {
  if (!items) return { total: 0, active: 0 };
  return {
    total: items.length,
    active: items.filter((i) => i.isActive).length,
  };
}

export function Analytics() {
  const { accessToken } = useAuthStore();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const [
        users,
        signalr,
        activities,
        aiProviders,
        prompts,
        storage,
        payment,
        social,
        crypto,
      ] = await Promise.all([
        fetchJson<Array<{ role: string }>>('/api/admin/users', accessToken),
        fetchJson<{ totalConnections: number; uniqueUsers: number }>(
          '/api/admin/signalr-connections',
          accessToken,
        ),
        fetchJson<unknown[]>('/api/admin/activity-logs?take=200', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/ai-providers', accessToken),
        fetchJson<unknown[]>('/api/admin/ai-prompts', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/storage-providers', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/payment-providers', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/auth-providers', accessToken),
        fetchJson<Array<{ isActive: boolean }>>('/api/admin/crypto-wallets', accessToken),
      ]);

      if (cancelled) return;

      if (!users) {
        setError('Analitik verileri yüklenemedi.');
        setLoading(false);
        return;
      }

      setOverview({
        users: users.length,
        admins: users.filter((u) => u.role === 'admin' || u.role === 'system').length,
        editors: users.filter((u) => u.role === 'editor').length,
        viewers: users.filter((u) => u.role === 'viewer').length,
        liveConnections: signalr?.totalConnections ?? 0,
        liveUsers: signalr?.uniqueUsers ?? 0,
        activityCount: activities?.length ?? 0,
        ai: countActive(aiProviders),
        storage: countActive(storage),
        payment: countActive(payment),
        social: countActive(social),
        crypto: countActive(crypto),
        prompts: prompts?.length ?? 0,
      });
      setLoading(false);
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  if (loading) {
    return <div className="admin-loading">Yükleniyor…</div>;
  }

  const o = overview;

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>Analitik</h1>
          <p>Mevcut admin API’lerinden üretilen sistem özeti — sahte metrik yok</p>
        </div>
      </header>

      {error ? <div className="admin-error">{error}</div> : null}

      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-icon" aria-hidden>
            <UsersIcon />
          </div>
          <div className="admin-stat-content">
            <div className="admin-stat-value">{o?.users ?? 0}</div>
            <div className="admin-stat-label">Kullanıcı</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" aria-hidden>
            <SignalIcon />
          </div>
          <div className="admin-stat-content">
            <div className="admin-stat-value">{o?.liveConnections ?? 0}</div>
            <div className="admin-stat-label">Canlı bağlantı ({o?.liveUsers ?? 0} kullanıcı)</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" aria-hidden>
            <ClipboardDocumentListIcon />
          </div>
          <div className="admin-stat-content">
            <div className="admin-stat-value">{o?.activityCount ?? 0}</div>
            <div className="admin-stat-label">Son etkinlik kaydı (max 200)</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-icon" aria-hidden>
            <CpuChipIcon />
          </div>
          <div className="admin-stat-content">
            <div className="admin-stat-value">
              {o?.ai.active ?? 0}
              <span className="admin-stat-suffix">/{o?.ai.total ?? 0}</span>
            </div>
            <div className="admin-stat-label">Aktif AI · {o?.prompts ?? 0} prompt</div>
          </div>
        </div>
      </div>

      <div className="admin-analytics-grid">
        <div className="admin-card">
          <div className="admin-card-header">
            <h2>Rol dağılımı</h2>
          </div>
          <div className="admin-card-body">
            <div className="admin-usage-list">
              <RoleBar label="Admin / System" count={o?.admins ?? 0} total={o?.users ?? 0} />
              <RoleBar label="Editor" count={o?.editors ?? 0} total={o?.users ?? 0} />
              <RoleBar label="Viewer" count={o?.viewers ?? 0} total={o?.users ?? 0} />
            </div>
            <div className="admin-empty-actions" style={{ marginTop: 20 }}>
              <Link to="/admin/users" className="admin-btn admin-btn-secondary">
                Kullanıcıları yönet
              </Link>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-header">
            <h2>Entegrasyon durumu</h2>
          </div>
          <div className="admin-card-body">
            <div className="admin-integration-list">
              <IntegrationRow
                icon={<CircleStackIcon />}
                label="Depolama"
                active={o?.storage.active ?? 0}
                total={o?.storage.total ?? 0}
                to="/admin/storage-providers"
              />
              <IntegrationRow
                icon={<KeyIcon />}
                label="Social Login"
                active={o?.social.active ?? 0}
                total={o?.social.total ?? 0}
                to="/admin/social-providers"
              />
              <IntegrationRow
                icon={<CpuChipIcon />}
                label="AI"
                active={o?.ai.active ?? 0}
                total={o?.ai.total ?? 0}
                to="/admin/ai-providers"
              />
              <IntegrationRow
                icon={<CreditCardIcon />}
                label="Ödeme"
                active={o?.payment.active ?? 0}
                total={o?.payment.total ?? 0}
                to="/admin/payment-providers"
              />
              <IntegrationRow
                icon={<WalletIcon />}
                label="Kripto"
                active={o?.crypto.active ?? 0}
                total={o?.crypto.total ?? 0}
                to="/admin/crypto-wallets"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="admin-card" style={{ marginTop: 20 }}>
        <div className="admin-empty-state admin-empty-state-compact">
          <p>
            Kullanım grafikleri, export formatları ve sistem health metrikleri için henüz
            backend telemetrisi yok. Sahte değerler gösterilmiyor.
          </p>
          <Link to="/admin/activity" className="admin-btn admin-btn-secondary">
            Etkinlik günlüğüne git
          </Link>
        </div>
      </div>
    </div>
  );
}

function RoleBar({ label, count, total }: { label: string; count: number; total: number }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="admin-usage-item">
      <div className="admin-usage-info">
        <span className="admin-usage-name">{label}</span>
        <span className="admin-usage-count">{count}</span>
      </div>
      <div className="admin-usage-bar-container">
        <div className="admin-usage-bar" style={{ width: `${pct}%` }} />
      </div>
      <span className="admin-usage-percentage">{pct}%</span>
    </div>
  );
}

function IntegrationRow({
  icon,
  label,
  active,
  total,
  to,
}: {
  icon: ReactNode;
  label: string;
  active: number;
  total: number;
  to: string;
}) {
  return (
    <Link to={to} className="admin-integration-row">
      <span className="admin-integration-icon" aria-hidden>
        {icon}
      </span>
      <span className="admin-integration-label">{label}</span>
      <span className="admin-integration-value">
        {active}/{total} aktif
      </span>
    </Link>
  );
}
